/**
 * Create or edit one catalogue item.
 *
 * The CSS field mirrors the server's sanitiser client-side so an operator sees
 * the refusal before a round trip — but the server's check is the security
 * boundary, not this one. The rules are not arbitrary: edition CSS is injected
 * into a Handlebars template compiled with noEscape into a page rendered by
 * Puppeteer, so an unscoped selector or a remote url() is a real primitive,
 * not a style nit.
 *
 * Confirm-gated, like every other mutating dialog in this section.
 *
 * ── TIER AVAILABILITY ──────────────────────────────────────────────────────
 * `PUT /catalogue/tiers/:tier/:kind` REPLACES one tier's whole grant list, and
 * the server refuses any result that breaks monotonicity — a lower tier may
 * never offer something a higher tier lacks. So a change that touches more
 * than one tier has to be applied in an order that is legal at EVERY step, not
 * merely at the end:
 *
 *   granting   → highest tier first, working DOWN
 *   revoking   → lowest tier first, working UP
 *
 * The opposite order trips the server's own guard halfway through and leaves
 * the tiers half-updated. `applyTierChanges` below is the only place this is
 * encoded, and the client pre-check refuses a non-upward-closed selection
 * before any request goes out.
 *
 * ── DELETE ─────────────────────────────────────────────────────────────────
 * Two-phase: the button arms, and only the second press sends. A hard delete
 * is offered ONLY when nothing uses the item; every server refusal (409
 * BUILTIN_ITEM / ITEM_IN_USE, 422 DEFAULT_ITEM) is shown in the operator's own
 * words rather than re-worded here.
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Alert, Typography, Box, FormControlLabel, Switch, Checkbox,
  FormGroup, Divider, CircularProgress,
} from '@mui/material';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { CatalogueItem, CatalogueKind, Tier } from '../weddingApi';
import { TIERS, TIER_META, num } from '../weddingFormat';

interface Props {
  open: boolean;
  kind: CatalogueKind;
  item: CatalogueItem | null;
  /**
   * Every item of this kind — INCLUDING retired ones.
   *
   * A tier PUT replaces the tier's whole list, so the list has to be rebuilt
   * from every sibling. Handing this component a filtered view would silently
   * revoke the grants of whatever was filtered out.
   */
  items: CatalogueItem[];
  onClose: () => void;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

/** Tier-gated kinds. Layouts are offered to everyone by design. */
const isTierGated = (kind: CatalogueKind): kind is 'style' | 'edition' => kind !== 'layout';

/**
 * The first tier that is NOT granted although a tier below it is — i.e. the
 * exact hole the server's monotonicity rule refuses. Null when the selection
 * is upward-closed.
 */
function firstMonotonicityGap(granted: Record<string, boolean>): Tier | null {
  let seenGrant = false;
  for (const t of TIERS) {
    if (granted[t]) seenGrant = true;
    else if (seenGrant) return t;
  }
  return null;
}

const HEX = /^#[0-9a-fA-F]{6}$/;
const KEY = /^[a-z][a-z0-9_-]{1,31}$/;

/** A cheap mirror of the server rule, for feedback only. */
function localCssComplaint(css: string, kind: CatalogueKind, key: string): string | null {
  if (!css.trim()) return null;
  const attr = kind === 'layout' ? 'data-layout' : `data-${kind}`;
  const scope = `[${attr}='${key}']`;
  const scopeD = `[${attr}="${key}"]`;
  const lower = css.toLowerCase();
  for (const bad of ['@import', '</style', '<script', 'javascript:', 'expression(', '-moz-binding', '@charset']) {
    if (lower.includes(bad)) return `Remove ${bad} — the server will refuse it.`;
  }
  const selectors = css.replace(/\/\*[\s\S]*?\*\//g, '').split('}')
    .map((c) => c.split('{')[0].trim()).filter((s) => s && !s.startsWith('@'));
  for (const sel of selectors) {
    for (const one of sel.split(',')) {
      const s = one.trim();
      if (s && !s.startsWith(scope) && !s.startsWith(scopeD)) {
        return `Every selector must start with ${scope} — found "${s.slice(0, 40)}".`;
      }
    }
  }
  return null;
}

const CatalogueItemDialog: React.FC<Props> = ({ open, kind, item, items, onClose, onSaved, onError }) => {
  const isEdit = item !== null;
  const [key, setKey] = React.useState('');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [primary, setPrimary] = React.useState('#1A5F7A');
  const [accent, setAccent] = React.useState('#C9A227');
  const [paper, setPaper] = React.useState('#FBF8F3');
  const [css, setCss] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [granted, setGranted] = React.useState<Record<string, boolean>>({});
  // Two-phase delete: `armed` is what the first press sets, and only the
  // second press issues a request. Nothing here calls the API on open.
  const [armed, setArmed] = React.useState<'soft' | 'hard' | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setKey(item ? item.item_key : '');
    setName(item ? item.name : '');
    setDescription(item ? item.description : '');
    setIsActive(item ? item.is_active : true);
    setPrimary(item?.palette_primary || '#1A5F7A');
    setAccent(item?.palette_accent || '#C9A227');
    setPaper(item?.palette_paper || '#FBF8F3');
    setCss((kind === 'edition' ? item?.print_css : item?.web_css) || '');
    setSaving(false);
    const seeded: Record<string, boolean> = {};
    for (const t of TIERS) seeded[t] = (item?.tiers || []).includes(t);
    setGranted(seeded);
    setArmed(null);
    setDeleting(false);
    setDeleteError(null);
  }, [open, item?.id, kind]);

  const cssComplaint = localCssComplaint(css, kind, key || 'new-item');
  const keyValid = isEdit || KEY.test(key);
  const paletteValid = kind !== 'style' || (HEX.test(primary) && HEX.test(accent) && HEX.test(paper));

  // ── Tier availability ────────────────────────────────────────────────────
  const tierGated = isTierGated(kind);
  const showTiers = tierGated && isEdit;
  const monotonicityGap = showTiers ? firstMonotonicityGap(granted) : null;
  const tiersChanged = showTiers
    && TIERS.some((t) => granted[t] !== (item?.tiers || []).includes(t));
  const inUse = Number(kind === 'layout' ? item?.couples_using : item?.books_using) || 0;

  /** This tier's whole grant list, rebuilt from every sibling of this kind. */
  const keysForTier = (tier: Tier, includeThisItem: boolean): string[] => {
    const others = items
      .filter((i) => i.item_key !== (item ? item.item_key : '') && (i.tiers || []).includes(tier))
      .map((i) => i.item_key);
    return includeThisItem && item ? [...others, item.item_key] : others;
  };

  /**
   * Apply the checkbox change one tier at a time, in the only order the
   * server's monotonicity guard accepts at every intermediate step:
   * grants highest-first, revocations lowest-first.
   */
  const applyTierChanges = async () => {
    // The literal comparison (rather than the `tierGated` alias) is what
    // narrows `kind` to the two tier-gated kinds setTierCatalogue accepts.
    if (!item || kind === 'layout') return;
    const before = new Set(item.tiers || []);
    const adds = TIERS.filter((t) => granted[t] && !before.has(t)).reverse(); // platinum → classic
    const removes = TIERS.filter((t) => !granted[t] && before.has(t)); // classic → platinum

    for (const tier of adds) {
      // eslint-disable-next-line no-await-in-loop
      await weddingApi.setTierCatalogue(tier, kind, keysForTier(tier, true));
    }
    for (const tier of removes) {
      // eslint-disable-next-line no-await-in-loop
      await weddingApi.setTierCatalogue(tier, kind, keysForTier(tier, false));
    }
  };

  const canSave = keyValid && name.trim().length > 0 && paletteValid && !cssComplaint
    && !monotonicityGap && !saving && !deleting;

  const runDelete = async (hard: boolean) => {
    if (!item) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const r = await weddingApi.deleteCatalogueItem(kind, item.item_key, hard);
      onSaved(r.deleted ? `${item.name} deleted.` : `${item.name} retired — it is no longer offered.`);
    } catch (e) {
      // The server's refusals are operator-quality copy: they name the count
      // and the remedy. Re-wording them here would lose both.
      setDeleteError((e as WeddingApiError).message || 'Could not delete the item');
      setDeleting(false);
      setArmed(null);
    }
  };

  const save = async () => {
    setSaving(true);
    const cssField = kind === 'edition' ? 'print_css' : 'web_css';
    try {
      if (isEdit) {
        await weddingApi.updateCatalogueItem(kind, item.item_key, {
          name: name.trim(),
          description,
          is_active: isActive,
          ...(kind === 'style'
            ? { palette_primary: primary, palette_accent: accent, palette_paper: paper }
            : {}),
          [cssField]: item.is_builtin ? undefined : (css || null),
        });
        if (tiersChanged) await applyTierChanges();
        onSaved(`${name.trim()} updated.`);
      } else {
        const r = await weddingApi.createCatalogueItem(kind, {
          item_key: key,
          name: name.trim(),
          description,
          is_active: isActive,
          ...(kind === 'style'
            ? { palette_primary: primary, palette_accent: accent, palette_paper: paper }
            : {}),
          [cssField]: css || null,
        });
        onSaved(r.appearance_note ? `${name.trim()} created. ${r.appearance_note}` : `${name.trim()} created.`);
      }
    } catch (e) {
      onError((e as WeddingApiError).message || 'Could not save the item');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${item.name}` : `New ${kind}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!isEdit && (
            <TextField
              label="Key" value={key} size="small" fullWidth disabled={saving}
              onChange={(e) => setKey(e.target.value)}
              error={key.length > 0 && !KEY.test(key)}
              helperText="Lower-case letters, digits, hyphen or underscore; starts with a letter. Permanent — it is stored on every book that uses this item."
            />
          )}
          <TextField label="Name" value={name} size="small" fullWidth disabled={saving}
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 80 }} />
          <TextField label="Description" value={description} size="small" fullWidth multiline minRows={2}
            disabled={saving} onChange={(e) => setDescription(e.target.value)} />

          {kind === 'style' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {([['Primary', primary, setPrimary], ['Accent', accent, setAccent], ['Paper', paper, setPaper]] as const).map(
                ([label, value, setter]) => (
                  <Stack key={label} direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: HEX.test(value) ? value : 'transparent' }} />
                    <TextField
                      label={label} value={value} size="small" fullWidth disabled={saving}
                      onChange={(e) => setter(e.target.value)}
                      error={!HEX.test(value)}
                    />
                  </Stack>
                )
              )}
            </Stack>
          )}

          {kind === 'style' && (
            <Typography variant="caption" color="text.secondary">
              Three colours is all that is needed — the remaining palette is
              derived at render time and walked against a contrast ladder, so an
              unreadable pairing is corrected rather than shipped.
            </Typography>
          )}

          <FormControlLabel
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={saving} />}
            label="Offered to coordinators"
          />

          {/* ── Tier availability ─────────────────────────────────────── */}
          {showTiers && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Tier availability</Typography>
              <FormGroup row>
                {TIERS.map((t) => (
                  <FormControlLabel
                    key={t}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!granted[t]}
                        disabled={saving || deleting}
                        onChange={(e) => setGranted((g) => ({ ...g, [t]: e.target.checked }))}
                        inputProps={{ 'aria-label': `${TIER_META[t].label} tier` }}
                      />
                    }
                    label={TIER_META[t].label}
                  />
                ))}
              </FormGroup>
              {monotonicityGap ? (
                <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                  A tier can never offer less than the tier below it — a couple who
                  upgraded would lose an option they already had.
                  {' '}<strong>{TIER_META[monotonicityGap].label}</strong> has to offer this
                  too. The server refuses the other arrangement; this is the same rule,
                  said before the round trip.
                </Alert>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Saving applies one change per tier — grants from the top tier down,
                  removals from the bottom up — because each step has to be valid on
                  its own, not just the final result.
                </Typography>
              )}
            </Box>
          )}
          {tierGated && !isEdit && (
            <Typography variant="caption" color="text.secondary">
              Tier availability is granted after the item exists — create it, then
              reopen it to choose which tiers are offered it.
            </Typography>
          )}

          {item?.is_builtin ? (
            <Alert severity="info" variant="outlined">
              This is a built-in. Its appearance ships inside the pdf image and
              the web bundle, so there is no stylesheet to edit here — but you
              can rename it, re-describe it, reorder it and retire it.
            </Alert>
          ) : (
            <>
              <TextField
                label={kind === 'edition' ? 'Print stylesheet (PDF)' : 'Stylesheet'}
                value={css} onChange={(e) => setCss(e.target.value)}
                size="small" fullWidth multiline minRows={8} disabled={saving}
                error={!!cssComplaint}
                helperText={
                  cssComplaint
                  || `${css.length} characters, 65536 max. Every selector must be scoped to [${kind === 'layout' ? 'data-layout' : `data-${kind}`}='${key || 'your-key'}']. Images must be inline base64 data: URLs.`
                }
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
              {!css.trim() && (
                <Alert severity="warning" variant="outlined">
                  {kind === 'edition'
                    ? 'Without a print stylesheet, books using this edition render unornamented — correct, but plain.'
                    : kind === 'layout'
                      ? 'Without a stylesheet this layout has no grid and renders as a single unstyled column.'
                      : 'Without a stylesheet this style uses the default treatment in the browser. Its palette still applies.'}
                </Alert>
              )}
            </>
          )}

          {/* ── Delete ────────────────────────────────────────────────── */}
          {isEdit && !item.is_builtin && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Remove this {kind}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                  Retiring stops it being offered; every book already made with it keeps
                  rendering it. Deleting outright is possible only while nothing uses it
                  {inUse > 0 ? ` — ${num(inUse)} in use right now` : ''}.
                </Typography>

                {deleteError && (
                  <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>{deleteError}</Alert>
                )}

                {armed === null ? (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" color="warning" variant="outlined" disabled={saving || deleting}
                      onClick={() => { setDeleteError(null); setArmed('soft'); }}>
                      Retire
                    </Button>
                    <Button size="small" color="error" variant="outlined"
                      disabled={saving || deleting || inUse > 0}
                      onClick={() => { setDeleteError(null); setArmed('hard'); }}>
                      Delete permanently
                    </Button>
                  </Stack>
                ) : (
                  <Alert severity={armed === 'hard' ? 'error' : 'warning'} variant="outlined">
                    {armed === 'hard'
                      ? `Delete ${item.name} permanently? This row is removed from the catalogue and cannot be brought back.`
                      : `Retire ${item.name}? It stops being offered to coordinators immediately.`}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" variant="contained" color={armed === 'hard' ? 'error' : 'warning'}
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : undefined}
                        onClick={() => runDelete(armed === 'hard')}>
                        {armed === 'hard' ? 'Delete permanently' : 'Retire it'}
                      </Button>
                      <Button size="small" disabled={deleting} onClick={() => setArmed(null)}>
                        Keep it
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}>
          {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogueItemDialog;
